import { useState } from 'react'
import { motion } from 'framer-motion'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Building2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { CreateOrganizationForm } from '@/features/organizations/forms/create-organization-form'
import { EditOrganizationForm } from '@/features/organizations/forms/edit-organization-form'
import { api } from '@/lib/api-client'
import { Organization } from '@/features/organizations/types'
import { useToast } from '@/hooks/use-toast'
import { AuthorizationGate } from '@/features/authorization/AuthorizationGate'
import { OrganizationCard } from '@/features/organizations/components/organization-card'
import { ImportExportButtons } from '@/components/ui/import-export-buttons'
import { useOrganizations } from '@/features/organizations/hooks'
import { useListState } from '@/hooks/use-list-state'
import { DataToolbar } from '@/components/ui/data-toolbar'
import { DataPagination } from '@/components/ui/data-pagination'
import { useQuery } from '@tanstack/react-query'

export function OrganizationsPage() {
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [orgToEdit, setOrgToEdit] = useState<Organization | null>(null)
  const [orgToDelete, setOrgToDelete] = useState<Organization | null>(null)
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const listState = useListState({ defaultPerPage: 6, defaultSortBy: 'created_at' })

  const { data: orgResponse, isLoading } = useOrganizations(listState.queryParams)

  // Fetch maturity overview so the cards get assessment/maturity data
  const { data: maturityOverview } = useQuery({
    queryKey: ['stats', 'maturity-overview'],
    queryFn: () => api.stats.getMaturityOverview().then(res => res.data),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.organizations.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] })
      queryClient.invalidateQueries({ queryKey: ['stats', 'maturity-overview'] })
      setOrgToDelete(null)
      toast({ title: 'Organization deleted', description: 'Organization has been deleted successfully.' })
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'Failed to delete organization',
        variant: 'destructive',
      })
    },
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  const organizations: Organization[] = orgResponse?.items ?? []
  const totalItems = orgResponse?.total ?? 0
  const totalPages = orgResponse?.pages ?? 1

  // Build a lookup from maturity overview so we can merge data
  const overviewMap = new Map<string, any>()
  if (maturityOverview?.organizations) {
    for (const o of maturityOverview.organizations) {
      overviewMap.set(o.id, o)
    }
  }

  return (
    <motion.div
      className="space-y-6"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
    >
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="min-w-0 flex-1">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:truncate sm:text-3xl sm:tracking-tight">
            Organizations
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Manage your organizations and their assessments
          </p>
        </div>
        <div className="mt-4 flex items-center gap-3 md:ml-4 md:mt-0">
          <ImportExportButtons
            entityType="organizations"
            entityLabel="Organizations"
          />
          <AuthorizationGate actions={['create_organization']}>
            <Button onClick={() => setShowCreateForm(true)}>
              <Plus className="mr-2 h-4 w-4" />
              New Organization
            </Button>
          </AuthorizationGate>
        </div>
      </div>

      {/* Toolbar */}
      <DataToolbar
        listState={listState}
        searchPlaceholder="Search organizations..."
        sortOptions={[
          { label: 'Name', value: 'name' },
          { label: 'Created', value: 'created_at' },
        ]}
        totalItems={totalItems}
        perPageOptions={[6, 12, 24]}
      />

      {/* Organizations Grid */}
      {organizations.length > 0 ? (
        <>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {organizations.map((org: Organization, i: number) => {
              const overview = overviewMap.get(org.id)
              const cardData = overview || {
                id: org.id,
                name: org.name,
                description: org.description,
                industry_id: org.industry_id,
                size: org.size,
                industry_name: org.industry_name,
              }

              return (
                <OrganizationCard
                  key={org.id}
                  org={cardData}
                  showActions
                  index={i}
                  onEdit={setOrgToEdit}
                  onDelete={setOrgToDelete}
                />
              )
            })}
          </div>

          {/* Pagination */}
          <DataPagination
            page={listState.page}
            totalPages={totalPages}
            onPageChange={listState.setPage}
            totalItems={totalItems}
            perPage={listState.perPage}
          />
        </>
      ) : (
        <div className="text-center py-12">
          <Building2 className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            {listState.search ? 'No matching organizations' : 'No organizations'}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {listState.search
              ? 'Try adjusting your search or filters.'
              : 'Get started by creating your first organization.'}
          </p>
          {!listState.search && (
            <AuthorizationGate actions={['create_organization']}>
              <div className="mt-6">
                <Button onClick={() => setShowCreateForm(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  New Organization
                </Button>
              </div>
            </AuthorizationGate>
          )}
        </div>
      )}

      {/* Create Organization Form */}
      {showCreateForm && (
        <CreateOrganizationForm
          onClose={() => setShowCreateForm(false)}
          onSuccess={() => {
            setShowCreateForm(false)
          }}
        />
      )}

      {/* Edit Organization Form */}
      {orgToEdit && (
        <EditOrganizationForm
          organization={orgToEdit}
          onClose={() => setOrgToEdit(null)}
          onSuccess={() => setOrgToEdit(null)}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!orgToDelete} onOpenChange={(open) => { if (!open) setOrgToDelete(null) }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Organization</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete &quot;{orgToDelete?.name}&quot;? This will also delete all associated assessments, answers. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOrgToDelete(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => orgToDelete && deleteMutation.mutate(orgToDelete.id)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete Organization'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  )
}
